/// <amd-module name="Dashboards/Samples/CatalogConfigSample" />
import * as React from "react";

import { ConfigPanel } from "Dashboards/ConfigHost/ConfigPanel";
import { WidgetSizeConverter } from "Dashboards/Grid/WidgetSizeConverter";
import { CatalogPanel } from "Dashboards/WidgetCatalogUI/CatalogPanel";
import { ConfigPanelParentProps } from "Dashboards/WidgetContracts/Internal/ConfigPanelProps";
import { DashboardWidgetProps } from "Dashboards/WidgetContracts/Internal/DashboardWidgetProps";
import { WidgetHostProps } from "Dashboards/WidgetContracts/Internal/WidgetHostProps";
import { WidgetPresentationMode, WidgetTeamContext } from "Dashboards/WidgetContracts/WidgetProps";
import { WidgetHost } from "Dashboards/WidgetHost/WidgetHost";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { getTeam } from "./DashboardsSamplesContext"

interface CatalogConfigSampleProps {}

interface CatalogConfigSampleState {
    showConfigPanel: boolean;
    /** "Saved" settings for widget for use outside of configuration. If preview is cancelled, roll back to this. */
    configSavedProps: DashboardWidgetProps | null;
    /**Props for widget to render during configuration preview mode. */
    configPreviewProps: DashboardWidgetProps | null;

    showCatalogPanel: boolean;

    teamContext: WidgetTeamContext | undefined;

    projectId: string | undefined;
}

export class CatalogConfigSample extends VssComponent<CatalogConfigSampleProps, CatalogConfigSampleState> {
    constructor(props: CatalogConfigSampleProps, context: IVssComponentContext) {
        super(props, context);
        this.state = this.getDefaultState();
    }

    public componentDidMount(): void {
        const pageServiceData = this.context.pageContext.getService<ITfsPageService>("ITfsPageService").getData();
        const projectId = (pageServiceData && pageServiceData.project && pageServiceData.project.id) || "";
        this.setState({
            teamContext: getTeam(this.context.pageContext),
            projectId: projectId
        });
    }

    public render(): JSX.Element | null {
        if (this.state.teamContext == null) {
            return null;
        }

        const widgetConfig = this.generateWidgetPropsFromState(this.state.teamContext);
        try {
            return (
                <div className="catalog-config-sample">
                    <div>
                        Dashboards <b>CatalogPanel</b> and <b>ConfigPanel</b> experiences allows for widgets to be selected and configured from a
                        dashboard. Below, we see a WidgetHost containing a widget, with edit experience suppressed by default. Note: Unlike a real
                        dashboard, this example only shows the most recently added widget from the catalog.
                    </div>
                    <br />
                    <Button primary onClick={this.showCatalog} text={"Choose Widget Type"} />

                    {widgetConfig !== null && (
                        <div
                            className="dashboard widget-shadow-enabled"
                            style={WidgetSizeConverter.ConvertToPixelSize(widgetConfig.widgetProps.size)}
                        >
                            <WidgetHost
                                key={widgetConfig.widgetProps.contributionId}
                                {...widgetConfig}
                                widgetPresentationMode={this.getMode()}
                                parentPageName="CatalogConfigSample"
                            />
                        </div>
                    )}

                    <CatalogPanel
                        isOpen={this.state.showCatalogPanel}
                        onDismiss={this.onCatalogDismiss}
                        onAdd={this.onCatalogAdd}
                        isValidDropPoint={(x, y) => {
                            return false;
                        }}
                        onDrop={() => {}}
                        projectId={this.state.projectId!}
                    />

                    {this.state.configSavedProps !== null && this.state.showConfigPanel && (
                        <ConfigPanel
                            config={this.state.configSavedProps}
                            parentProps={this.generateConfigPropsFromSettings()}
                            projectId={this.state.projectId!}
                            teamContext={this.state.teamContext}
                        />
                    )}
                </div>
            );
        } catch (e) {
            return <div>{e}</div>;
        }
    }

    private getDefaultState(): CatalogConfigSampleState {
        return {
            showCatalogPanel: false,
            showConfigPanel: false,
            configSavedProps: null,
            configPreviewProps: null,
            teamContext: undefined,
            projectId: undefined
        };
    }

    private getMode(): WidgetPresentationMode {
        return this.state.showConfigPanel ? WidgetPresentationMode.dashboardConfigPreview : WidgetPresentationMode.dashboardEditView;
    }

    // Catalog Handlers and support code.
    private showCatalog = () => {
        return this.setState({ showCatalogPanel: true });
    };

    private onCatalogDismiss = () => {
        return this.setState({ showCatalogPanel: false });
    };

    private onCatalogAdd = (initialWidgetSettings: DashboardWidgetProps) => {
        return this.setState({
            showCatalogPanel: false,
            configSavedProps: initialWidgetSettings
        });
    };

    // Widget Host Handlers and support code.

    private generateWidgetPropsFromState(teamContext: WidgetTeamContext): WidgetHostProps | null {
        // Use settings props
        const widgetConfig: DashboardWidgetProps | null = this.state.showConfigPanel ? this.state.configPreviewProps : this.state.configSavedProps;

        return (
            widgetConfig && {
                openLinksInNewTab: false,
                userCanConfigure: true,
                widgetHostParentProps: {
                    onConfigureWidget: this.showConfig,
                    onDeleteWidget: this.onDeleteWidget,
                    onCopyWidget: () => {
                        alert(
                            "At this point, the page can perform a 'blind' dashboard Widget Add to an unspecified position, or do a more elaborate in memory add of a widget."
                        );
                    },
                    onWidgetLoaded: () => {
                        /* No-op */
                    }
                },
                widgetPresentationMode: WidgetPresentationMode.dashboardView,
                parentPageName: "CatalogConfigSample",
                widgetProps: widgetConfig,
                convertToPixelSize: WidgetSizeConverter.ConvertToPixelSize,
                teamContext
            }
        );
    }

    private onDeleteWidget = () => {
        return this.setState({ configSavedProps: null });
    };

    // Config Handlers and support code.
    private generateConfigPropsFromSettings(): ConfigPanelParentProps {
        return {
            onDismiss: this.onConfigDismiss,
            onSave: this.onConfigSave,
            onUpdate: this.onUpdate
        };
    }

    private showConfig = () => {
        return this.setState({
            showConfigPanel: true,
            configPreviewProps: this.state.configSavedProps
        });
    };

    private onConfigDismiss = () => {
        return this.setState({ showConfigPanel: false });
    };

    private onConfigSave = (newSettings: DashboardWidgetProps) => {
        return this.setState({
            showConfigPanel: false,
            configSavedProps: newSettings
        });
    };
    private onUpdate = (newSettings: DashboardWidgetProps) => {
        return this.setState({
            configPreviewProps: newSettings
        });
    };
}
